
import VueFilters from "@/vue-filters";
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  CashOrderApprovalStatusEnum,
  ICashOrderDTO,
  IField,
  IRequestedAmount,
  ISolCustomerCurrency,
  ISolCustomerPack,
  IUser,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
} from "@/entities";
import { Getter } from "vuex-class";
import { ISolServiceOrderRequest, ISolServiceOrderRequestedAmount } from "@/services";

@Component({
  name: "approvals-table",
  mixins: [VueFilters],
})
export default class ApprovalsTable extends Vue {
  @Getter("getLoggedUser") loggedUser: IUser | undefined;
  @Prop({ required: true }) title!: string;
  @Prop({ required: true }) orders!: ICashOrderDTO[];
  @Prop({ required: true }) packs!: ISolCustomerPack[];
  @Prop({ required: true }) statusColor!: string;
  @Prop({ required: false, default: false }) showServiceCode!: boolean;
  @Prop({ required: false, default: true }) collapsed!: boolean;
  @Prop({ required: false, default: false }) showApprovalDate!: boolean;
  @Prop({ required: false, default: false }) showApprovalActions!: boolean;

  @Getter("getSolCustomerCurrencies") currencies!: ISolCustomerCurrency[];

  collapsedLocal = true;

  mounted(): void {
    this.collapsedLocal = this.collapsed;
  }

  get visibleFields(): IField[] {
    return this.fields.filter((field) => field.visible !== false);
  }

  isOwnOrder(order: ICashOrderDTO): boolean {
    return order.user?.username === this.loggedUser?.username;
  }

  get fields(): IField[] {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "orderType",
        label: this.$t("column.orderType") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "serviceCode",
        label: this.$t("column.id") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        visible: this.showServiceCode,
      },
      {
        key: "servicePoint",
        label: this.$t("label.servicepoint") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "createdAt",
        label: this.$t("column.created") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "serviceDate",
        label: this.$t("column.requested") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "updatedAt",
        label: this.$t("column.updated") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "approvalDate",
        label: this.$t("column.approval_date") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        visible: this.showApprovalDate,
      },
      {
        key: "username",
        label: this.$t("label.iduser") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "approvalStatus",
        label: this.$t("column.status") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "requestedAmounts",
        label: this.$t("column.amount") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        formatter: (_value: unknown, _key: string, item: ICashOrderDTO) => {
          const requestedAmounts = this.toSolServiceOrderRequest(
            item.message || "{}"
          ).requestedAmounts;
          if (!requestedAmounts?.length) {
            return [];
          }

          return this.sumRequestedAmounts(requestedAmounts);
        },
      },
    ];
  }

  resolveOrderType(productCode?: string): string {
    const orderType = resolveSolCustomerOrderType(productCode);
    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType);

    if (!descriptionKey) {
      return productCode || "";
    }

    return this.$t(descriptionKey).toString();
  }

  statusLabel(status: CashOrderApprovalStatusEnum): string {
    switch (status) {
      case CashOrderApprovalStatusEnum.PENDING:
        return "label.pending_approval";
      case CashOrderApprovalStatusEnum.APPROVED:
        return "label.approved_approval";
      case CashOrderApprovalStatusEnum.REJECTED:
        return "label.rejected_approval";
      case CashOrderApprovalStatusEnum.EXPIRED:
        return "label.expired_approval";
      default:
        return String(status);
    }
  }

  toSolServiceOrderRequest(message: string): ISolServiceOrderRequest {
    try {
      return JSON.parse(message) as ISolServiceOrderRequest;
    } catch {
      return {} as ISolServiceOrderRequest;
    }
  }

  openDetail(order: ICashOrderDTO): void {
    this.$emit("open-detail", order);
  }

  sumRequestedAmounts(requestedAmounts: ISolServiceOrderRequestedAmount[]): IRequestedAmount[] {
    const amountsByCurrency: IRequestedAmount[] = [];

    requestedAmounts?.forEach((requestedAmount) => {
      this.sumRequestedPacks(requestedAmount, amountsByCurrency);

      const amount = requestedAmount.totalAmount;

      if (amount) {
        const currencyIsoCode = this.resolveCurrencyIsoCode(requestedAmount);
        const currencySymbol = this.resolveCurrencySymbol(requestedAmount);

        const amountByCurrency = amountsByCurrency.find(
          (requestedAmount) => requestedAmount.currencyIsoCode === currencyIsoCode
        );

        if (amountByCurrency) {
          amountByCurrency.totalAmount = (amountByCurrency.totalAmount || 0) + amount;
        } else {
          amountsByCurrency.push({
            totalAmount: amount,
            currencyIsoCode,
            currencySymbol,
          });
        }
      }
    });
    return amountsByCurrency;
  }

  sumRequestedPacks(
    requestedAmount: ISolServiceOrderRequestedAmount,
    amountsByCurrency: IRequestedAmount[]
  ): void {
    if (requestedAmount.packs?.length) {
      requestedAmount.packs.forEach((pack) => {
        const packInfo = this.packs.find((p) => p.id === pack.id);

        packInfo?.denominations?.forEach((denomination) => {
          const currencyIsoCode = denomination.currencyIsoCode || "";
          const currencySymbol = denomination.currencySymbol || "";
          const amountByCurrency = amountsByCurrency.find(
            (requestedAmount) => requestedAmount.currencyIsoCode === currencyIsoCode
          );
          const amount =
            (denomination.value || 0) * (denomination.quantity || 0) * (pack.quantity || 0);

          if (amountByCurrency) {
            amountByCurrency.totalAmount = (amountByCurrency.totalAmount || 0) + amount;
          } else {
            amountsByCurrency.push({
              totalAmount: amount,
              currencyIsoCode,
              currencySymbol,
            });
          }
        });
      });
    }
  }

  resolveCurrencyIsoCode(requestedAmount: ISolServiceOrderRequestedAmount): string {
    if (requestedAmount.packs?.length) {
      const pack = this.packs.find((pack) => pack.id === requestedAmount.packs?.[0].id);

      return pack?.denominations?.[0].currencyIsoCode || "";
    }

    const currencyId = requestedAmount.currencyId;
    if (!currencyId) {
      return "";
    }

    const currency = this.currencies?.find((c) => String(c.currencyId) === String(currencyId));
    return currency?.isoCode || "";
  }

  resolveCurrencySymbol(requestedAmount: ISolServiceOrderRequestedAmount): string {
    const currencyId = requestedAmount.currencyId;
    if (!currencyId) {
      return "";
    }

    const currency = this.currencies?.find((c) => String(c.currencyId) === String(currencyId));
    return currency?.symbol || "";
  }

  resolveServicePointDescription(servicePoint?: {
    codeSol?: string;
    description?: string;
  }): string {
    if (!servicePoint) {
      return "";
    }
    const { codeSol, description } = servicePoint;
    if (codeSol && description) {
      return `${codeSol} - ${description}`;
    }
    return description || codeSol || "";
  }

  approveOrder(order: ICashOrderDTO): void {
    this.$emit("approve-order", order);
  }

  rejectOrder(order: ICashOrderDTO): void {
    this.$emit("reject-order", order);
  }
}
